// Lightweight in-order subsequence fuzzy matcher — no dependency needed for
// a dataset of tens of brands. "adi" matches "adidas" (prefix, contiguous),
// scores higher than "adi" matching "Saudi Denim" (mid-string, broken up).
// Returns null when the query's characters don't all appear, in order,
// somewhere in the target.

/** Higher is a better match. Null means "does not match at all." */
export function fuzzyScore(query: string, target: string): number | null {
  const q = query.trim().toLowerCase();
  const t = target.toLowerCase();
  if (q.length === 0) return 0;

  let score = 0;
  let qi = 0;
  let lastMatchIndex = -1;
  let consecutiveRun = 0;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] !== q[qi]) continue;

    if (ti === 0 || t[ti - 1] === ' ') score += 8; // start-of-string or word-boundary bonus
    if (lastMatchIndex === ti - 1) {
      consecutiveRun += 1;
      score += 4 + consecutiveRun; // escalating bonus for longer contiguous runs
    } else {
      consecutiveRun = 0;
    }
    score += 1;
    lastMatchIndex = ti;
    qi += 1;
  }

  if (qi < q.length) return null; // not every query character was found, in order

  score -= t.length * 0.05; // mild preference for shorter/more precise targets
  return score;
}

/** Filters items to fuzzy matches against `query` (via getText), sorted best-match-first. Ties broken alphabetically. */
export function fuzzyFilterSort<T>(query: string, items: T[], getText: (item: T) => string): T[] {
  return items
    .map((item) => ({ item, score: fuzzyScore(query, getText(item)) }))
    .filter((r): r is { item: T; score: number } => r.score !== null)
    .sort((a, b) => b.score - a.score || getText(a.item).localeCompare(getText(b.item)))
    .map((r) => r.item);
}
